var _ = require('underscore');
var TorsoView = require('backbone-torso/modules/View');
var paginationTemplate = require('./paginationTemplate.hbs');
var numberUtils = require('./../../utils/numberUtils');

/**
 * View for the pagination widget
 *
 * @param options {Object} various page options passed from the parent view.
 *   @param options.tableState {Torso.Cell} the shared state of the widget (filters, search, paging, fetching... etc.).
 *   @param [options.ariaControls=''] {String} the id of the table associated with this widget.
 */
module.exports = TorsoView.extend({
  tagName: 'section',
  className: 'brec-pagination',
  attributes: function() {
    return {
      'aria-controls': this._ariaControls,
      'aria-label': 'pagination'
    };
  },
  template: paginationTemplate,

  events: {
    'click [data-page-number]': '_pushPage'
  },

  maxNumPagingButtons: 7,

  /**
   * @method constructor
   * @param options {Object} various page options passed from the parent view.
   *   @param options.tableState {Torso.Cell} the shared state of the widget (filters, search, paging, fetching... etc.).
   *   @param [options.ariaControls=''] {String} the id of the table associated with this widget.
   *   @param [options.maxNumPagingButtons=7] {Number} the maximum number of paging buttons to display.
   * @override
   *
   * Override so that we can use ariaControls dynamically when
   * the page is initially rendered.
   */
  constructor: function(options) {
    var defaultOptions = {
      tableState: null,
      ariaControls: '',
      maxNumPagingButtons: this.maxNumPagingButtons
    };
    options = _.defaults({}, options, defaultOptions);
    this._ariaControls = options.ariaControls;
    this.maxNumPagingButtons = options.maxNumPagingButtons;

    TorsoView.apply(this, arguments);

    this._tableState = options.tableState;
    this._pullPagingInfo();

    this.listenTo(this._tableState, 'change:page change:totalPages', this._pullPagingInfo);
    this.listenTo(this._tableState, 'change:totalPages change:page', this.render);
  },

  /**
   * @method prepare
   * @overrid
   */
  prepare: function() {
    var templateContext = TorsoView.prototype.prepare.apply(this);
    templateContext.tableState = this._tableState.toJSON();
    return templateContext;
  },

  /**
   * @method _pullPagingInfo
   * @private
   */
  _pullPagingInfo: function() {
    var currentPage = this._tableState.get('page');
    currentPage = numberUtils.convertToNumberWithMinimum(currentPage, 1);

    var totalPages = this._tableState.get('totalPages');
    totalPages = numberUtils.convertToNumberWithMinimum(totalPages, 1);

    var nextPage = null;
    var atMaxPage = (currentPage >= totalPages);
    if (!atMaxPage) {
      nextPage = currentPage + 1;
    }

    var prevPage = null;
    var atMinPage = (currentPage <= 1);
    if (!atMinPage) {
      prevPage = currentPage - 1;
    }

    // Button Layout:
    // [1] [2|...] [ center button group may have 0 or more buttons ] [nextToLast|...] [last]
    // The buttons are filled based on the number of total pages in this order:
    // 1, 2, last, nextToLast, center group
    // The # of buttons in the center group is variable based on the number of total buttons (# total buttons - 4)

    // Buttons is an array where each value is either 'ellipsis', a page # or false if the button shouldn't be shown.
    var buttons = [];

    var firstButtonIndex = 0;
    var secondButtonIndex = 1;
    var centerLeftButtonIndex = 2;
    var centerRightButtonIndex = this.maxNumPagingButtons - 3;
    var nextToLastButtonIndex = this.maxNumPagingButtons - 2;
    var lastButtonIndex = this.maxNumPagingButtons - 1;

    buttons[firstButtonIndex] = 1; // There is always a page 1 button.

    // The 2nd page button is shown (may later be changed to ellipsis) if there are 2 or more pages.
    if (totalPages >= 2) {
      buttons[secondButtonIndex] = 2;
    }

    // There is always a last page if there are 3 or more pages.
    if (totalPages >= 3) {
      buttons[lastButtonIndex] = totalPages;
    }

    // The next to last page button is shown (may later be changed to ellipsis) if there are 4 or more pages.
    if (totalPages >= 4) {
      buttons[nextToLastButtonIndex] = totalPages - 1;
    }

    // Calculate center button group only if there are 5 or more pages.
    if (totalPages >= 5) {
      var numFixedLeftButtons = 2;
      var numFixedRightButtons = 2;
      var maxNumCenterButtons = this.maxNumPagingButtons - numFixedLeftButtons - numFixedRightButtons;
      var halfNumCenterButtons = Math.floor(maxNumCenterButtons / 2); // This is the max number of buttons on either side of the current page.

      buttons[centerLeftButtonIndex] = currentPage - halfNumCenterButtons;
      buttons[centerRightButtonIndex] = currentPage + halfNumCenterButtons;

      // Limit the center left button to always show a full block of center buttons if the page is near the last page.
      var numRightButtonsIncludingCenterButtons = maxNumCenterButtons + numFixedRightButtons - 1; // -1 because this is a button in the group.
      buttons[centerLeftButtonIndex] = Math.min(buttons[centerLeftButtonIndex], totalPages - numRightButtonsIncludingCenterButtons);
      // Limit the center left button to ultimately be 3 or greater.
      var numLeftButtonsIncludingCenterLeft = numFixedLeftButtons + 1;
      buttons[centerLeftButtonIndex] = Math.max(buttons[centerLeftButtonIndex], numLeftButtonsIncludingCenterLeft);

      // Limit the center right button to always show a full block of center buttons if the page is near the first page.
      var numLeftButtonsIncludingCenterButtons = numFixedLeftButtons + maxNumCenterButtons;
      buttons[centerRightButtonIndex] = Math.max(buttons[centerRightButtonIndex], numLeftButtonsIncludingCenterButtons);

      // Limit the center right button to be < the total number of pages - 2
      buttons[centerRightButtonIndex] = Math.min(buttons[centerRightButtonIndex], totalPages - numFixedRightButtons);

      // Limit the center right button to ultimately be greater than the center left button or turned off.
      if (buttons[centerRightButtonIndex] <= buttons[centerLeftButtonIndex]) {
        buttons[centerRightButtonIndex] = false;
      }

      // If there is a center right button, then fill in the page numbers for the center group of buttons.
      // If buttons[centerRightButtonIndex] = false then centerButtonPageValue < buttons[centerRightButtonIndex] will be false and no buttons will be populated.
      // Any button slots not populated by this for loop will remain false and not be displayed.

      // Total number of center buttons is either limited by the total number of pages or the limits on the # of buttons.
      var numCenterButtons = Math.min(maxNumCenterButtons, totalPages - numFixedLeftButtons - numFixedRightButtons);
      var centerButtonIndexOffset = centerLeftButtonIndex;
      var centerButtonValueOffset = buttons[centerLeftButtonIndex];
      // -1 to not populating the centerRight button because it is populated above.
      for(var index = 1; index < numCenterButtons - 1; index++) {
        buttons[index + centerButtonIndexOffset] = index + centerButtonValueOffset;
      }

      // If there is a gap between the 2nd button and the centerLeft button, then convert the 2nd button to ellipsis.
      if (buttons[centerLeftButtonIndex] > buttons[secondButtonIndex] + 1) {
        buttons[secondButtonIndex] = 'ellipsis';
      }

      // If there is a gap between the next to last button and the centerRight button, then convert the next to last button to ellipsis.
      // The right-most center button may be the left button if there is only 1 center button.
      var centerRightButtonValue = buttons[centerRightButtonIndex] || buttons[centerLeftButtonIndex];
      if (centerRightButtonValue < buttons[nextToLastButtonIndex] - 1) {
        buttons[nextToLastButtonIndex] = 'ellipsis';
      }
    }

    this.set({
      atMaxPage: atMaxPage,
      atMinPage: atMinPage,
      prevPage: prevPage,
      nextPage: nextPage,
      buttons: buttons
    });

    this._tableState.set({
      page: currentPage,
      totalPages: totalPages
    });
  },

  /**
   * @method _broadcastPage
   * @param event {Torso.Event} the jQuery event that triggered this method.
   *
   * Triggers an action on the tableState from the parent widget
   * that tells it we're changing to whatever page number was
   * clicked in the view.
   */
  _pushPage: function(event) {
    var newPage = $(event.currentTarget).attr('data-page-number');
    this._tableState.set('page', newPage);
    return false;
  }
});
